import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app.store';
import {
  clearModal,
  closeModal as closeModalAction,
  initModal,
  openModal as openModalAction,
  selectModalScope,
} from './discloser.slice';
import type {
  DiscloserScopeState,
  ModalType,
  UseDiscloserOptions,
  UseDiscloserReturn,
} from './discloser.types';

export function useDiscloserHook<TypeT extends string = never, DataT = any>(
  scope: string,
  reRender?: () => void,
  options: UseDiscloserOptions = {},
): UseDiscloserReturn<TypeT, DataT> {
  const { persistOnUnmount = false } = options;
  const dispatch = useAppDispatch();

  // Get modal state for scope
  const scopeState = useAppSelector((state) =>
    selectModalScope(state, scope),
  ) as DiscloserScopeState<TypeT, DataT>;

  // Format config object
  const config: ModalType<TypeT, DataT> = {
    open: scopeState?.open ?? false,
    type: scopeState?.type,
    data: scopeState?.data,
  };

  // Cleanup on unmount
  useEffect(() => {
    dispatch(initModal({ scope }));
    return () => {
      if (!persistOnUnmount) {
        dispatch(clearModal({ scope }));
      }
    };
  }, [dispatch, scope, persistOnUnmount]);

  // Open modal handler
  const openModal = useCallback(
    <T extends DataT>(params: Omit<ModalType<TypeT, T>, 'open'>) => {
      const { type, data } = params;
      dispatch(openModalAction({ scope, type, data }));
    },
    [dispatch, scope],
  );

  // Close modal handler
  const closeModal = useCallback(
    (shouldReload?: boolean) => {
      dispatch(closeModalAction({ scope }));
      if (shouldReload && reRender) {
        reRender();
      }
    },
    [dispatch, scope, reRender],
  );

  return [config, openModal, closeModal] as const;
}

export default useDiscloserHook;
